/* eslint-disable react/jsx-props-no-spreading */
import { SaveOutlined } from '@ant-design/icons';
import {
    Button, Card, Col, Form, FormInstance, notification, Row, Space, Spin,
} from 'antd';
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { produce } from 'immer';
import { parseFormValues } from '../helpers/form';
import { useApiFetch } from '../hooks/api/useApiFetch';
import { IUseModalEdit } from '../hooks/table/useModalEdit';
import FormItems from '../components/form-items';
import { IFormAction, IFormLayout, TFormItem } from '../types/layouts/form';
import Select from '../components/select';

interface IDataFormProps {
    params: IFormLayout;
    modalEdit?: IUseModalEdit;
    objectName: string;
}

export interface IFormItemProps {
    formDataLoaded: boolean;
    formRef: FormInstance<any>;
    formKey: string;
    hidden?: boolean;
    onChange?: any;
    style?: any;
}

const FormFactory = ({ params, modalEdit, objectName }: IDataFormProps) => {
    const {
        fields, formDataLink, hideSaveButton, title, nextAction, primaryKey, inModal, viewMode,
    } = params;

    const [dataLoading, setDataLoading] = useState(false);
    const [dataLoaded, setDataLoaded] = useState(false);
    const [saveLoading, setSaveLoading] = useState(false);
    const [hiddenFields, setHiddenFields] = useState<Record<string, boolean>>({});

    const api = useApiFetch();

    const { objectId } = useParams();

    const [form] = Form.useForm();

    const navigate = useNavigate();

    const getFormData = async (id?: string|number) => {
        if (['string', 'number'].includes(typeof id)) {
            setDataLoading(true);

            let url = `/${objectName}/get/${id}?copy=${!!modalEdit?.copy}`;

            if (typeof formDataLink === 'string') {
                url = formDataLink;

                if (typeof modalEdit?.objectId !== 'undefined') {
                    url = formDataLink.replace('{objectId}', modalEdit.objectId.toString());
                } else if (typeof id !== 'undefined') {
                    url = formDataLink.replace('{objectId}', id.toString());
                }

                url = `${url}?copy=${!!modalEdit?.copy}`;
            }

            const result = await api.fetchData<any>(url, 'get', true);

            if (result.success) {
                form.setFieldsValue(parseFormValues(result.data, fields, true));
            } else {
                notification.error({ message: result.message });
            }

            setDataLoading(false);
        } else {
            form.resetFields();
        }
        setDataLoaded(true);
    };

    const getInitialFormValues = () => {
        const initialValues: Record<string, any> = {};
        fields.forEach((field) => {
            if (typeof field?.config?.defaultValue !== 'undefined') {
                initialValues[field.name] = field.config.defaultValue;
            }
        });
        return initialValues;
    };

    useEffect(() => {
        getFormData(modalEdit?.objectId || objectId);
    }, []);

    const onActionClick = async (action: IFormAction) => {
        try {
            const values = action.validate === false ? await form.getFieldsValue() : await form.validateFields();
            onFinish(values, action);
        } catch (err) {
            console.log(err);
        }
    };

    const onFinish = async (values: Record<string, any>, action?: IFormAction) => {
        setSaveLoading(true);

        let apiUrl = action?.url || `/${objectName}`;
        const isNewRow = modalEdit?.copy || (typeof objectId === 'undefined' && typeof modalEdit?.objectId === 'undefined');
        const targetId = modalEdit?.objectId || objectId;

        if (typeof action?.url === 'undefined') {
            apiUrl += isNewRow ? `/create?copy=${modalEdit?.copy}` : `/update/${targetId}`;
        } else if (!isNewRow && !action.ignorePrimaryKey) {
            apiUrl += `/${targetId}`;
        }

        let body: Record<string, any> = parseFormValues(values, fields);
        const headers: Record<string, any> = {};

        if (params.bodyType === 'form-data') {
            const formData = new FormData();
            headers['Content-Type'] = 'multipart/form-data';

            Object.keys(body).forEach((key) => {
                const fieldParams = fields.find((field) => field.name === key);
                // @ts-ignore
                if (fieldParams?.type === 'upload') {
                    if (Array.isArray(body[key])) {
                        body[key].forEach((file: any) => {
                            formData.append(key, file);
                        });
                    }
                } else {
                    switch (typeof body[key]) {
                        case 'boolean': {
                            formData.append(key, body[key] ? '1' : '0');
                            break;
                        }
                        default: {
                            if (typeof body[key] !== 'undefined') {
                                formData.append(key, body[key] || '');
                            }
                            break;
                        }
                    }
                }
            });

            body = formData;
        }

        setSaveLoading(false);

        const result = await api.fetchData<any>(apiUrl, 'post', true, body, false, headers);

        if (result.success) {
            notification.success({ message: result.message });

            if (inModal) {
                modalEdit?.updateTableData();
                modalEdit?.hide();
                // await getFormData(targetId);
                if (nextAction === 'reload') {
                    window.location.reload();
                }
            } else if (typeof nextAction === 'string') {
                const path = nextAction.replace(':primaryKey', result.data[primaryKey]);
                navigate(`/${objectName}/${path}`);
            }
        } else if (![400, 401, 403, 404].includes(result.status)) {
            notification.error({ message: result.message });
        }

        setSaveLoading(false);
    };

    const onValuesChange = (values: any) => {
        const key = Object.keys(values)[0];

        if (typeof key === 'string') {
            // @ts-ignore
            const dFields = fields.filter((el) => el.dependingFields?.filter((df) => df.name === key)?.length > 0);
            const nextState = produce(hiddenFields, (draft) => {
                if (dFields.length > 0) {
                    dFields.forEach((df) => {
                        let hidden = false;
                        if (Array.isArray(df.dependingFields)) {
                            df.dependingFields.forEach((el) => {
                                hidden = hidden ? true : !(el.values.includes(form.getFieldValue(el.name)));
                            });
                        }
                        draft[df.name] = hidden;
                    });
                }
            });
            setHiddenFields(nextState);
        }
    };

    const colProps: Record<string, number> = {
        xs: 24,
    };

    if (!inModal) {
        colProps.lg = 6;
        colProps.md = 8;
        colProps.sm = 12;
    }

    const renderFormEditor = (field: TFormItem) => {
        const Editor: React.FC<IFormItemProps> = FormItems[field.type];

        switch (field.type) {
            case 'select': {
                return (
                    <Select
                        {...field.config}
                        formRef={form}
                        formKey={field.name}
                        formDataLoaded={dataLoaded}
                    />
                );
            }
        }

        return <Editor {...field.config} formRef={form} formKey={field.name} formDataLoaded={dataLoaded} hidden={field.hidden} />;
    };

    const renderFields = () => fields?.map((field) => {
        let hidden = false;

        if (Array.isArray(field.dependingFields)) {
            field.dependingFields.forEach((el) => {
                hidden = hidden || !el.values.includes(form.getFieldValue(el.name));
            });
        }

        return (
            <Col key={field.name} {...colProps}>
                <Form.Item
                    label={field.title}
                    required={viewMode ? false : field.required}
                    rules={[{ required: hiddenFields[field.name] || field.hidden || hidden ? false : field.required }]}
                    name={field.name}
                    hidden={hiddenFields[field.name] || field.hidden || hidden}
                >
                    {renderFormEditor(field)}
                </Form.Item>
            </Col>
        );
    });

    const renderForm = () => (
        <fieldset disabled={viewMode}>
            <Form form={form} layout="vertical" initialValues={getInitialFormValues()} onFinish={onFinish} onValuesChange={onValuesChange}>
                <Row gutter={24}>
                    {renderFields()}
                </Row>
                <Form.Item hidden={viewMode}>
                    <Space direction="horizontal">
                        {!hideSaveButton && (
                            <Button htmlType="submit" icon={<SaveOutlined />} type="primary" loading={saveLoading}>
                                Сохранить
                            </Button>
                        )}
                        {params.actions?.map((action) => (
                            <Button htmlType="button" type="primary" {...action.config} loading={saveLoading} onClick={() => onActionClick(action)}>
                                {action.title}
                            </Button>
                        ))}
                    </Space>
                </Form.Item>
            </Form>
        </fieldset>
    );

    return (
        <Spin spinning={dataLoading}>
            {inModal ? renderForm() : (
                <Card title={title}>
                    {renderForm()}
                </Card>
            )}
        </Spin>
    );
};

FormFactory.defaultProps = {
    modalEdit: undefined,
};

export default FormFactory;
